<template>
  <article
    class="challenge-unit-card"
    :class="`challenge-unit-card--${status}`"
  >
    <span class="challenge-unit-card__ribbon">{{ statusLabel }}</span>
    <div class="challenge-unit-card__body">
      <span class="challenge-unit-card__badge">{{ unitLabel }}</span>
      <div class="challenge-unit-card__heading">
        <h3 class="challenge-unit-card__title">{{ title }}</h3>
        <span
          v-if="category"
          class="challenge-unit-card__category"
          :class="`challenge-unit-card__category--${category.toLowerCase()}`"
        >
          {{ category }}
        </span>
      </div>
      <p class="challenge-unit-card__description">{{ description }}</p>
    </div>
    <ActionButton
      variant="primary"
      size="large"
      full-width
      custom-class="challenge-unit-card__button"
      :disabled="!to"
      @click="onOpen"
    >
      Open Challenge
      <template #icon-right>
        <span class="challenge-unit-card__chevron">
          <ChevronRightIcon color="currentColor" />
        </span>
      </template>
    </ActionButton>
  </article>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { useRouter } from "vue-router";
import ActionButton from "@/components/common/ActionButton.vue";
import { ChevronRightIcon } from "@/components/icons";

const STATUS_LABELS = {
  completed: "Completed",
  "in-progress": "In-progress",
  "not-started": "Not started yet",
};

const props = defineProps({
  unitLabel: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  category: {
    type: String,
    default: "",
    validator: (value) => ["", "Simulation", "Device", "AI"].includes(value),
  },
  status: {
    type: String,
    default: "not-started",
    validator: (value) =>
      ["completed", "in-progress", "not-started"].includes(value),
  },
  to: {
    type: String,
    default: "",
  },
});

const router = useRouter();

const statusLabel = computed(() => STATUS_LABELS[props.status]);

const onOpen = () => {
  if (props.to) {
    router.push(props.to);
  }
};
</script>

<style scoped>
.challenge-unit-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-height: 404px;
  padding: 24px;
  background: var(--white);
  border: 2px solid var(--neutral-300);
  border-radius: 24px;
  overflow: visible;
}

.challenge-unit-card--completed {
  border-color: #03925e;
}

.challenge-unit-card--in-progress {
  border-color: var(--blue-light-1);
}

.challenge-unit-card__ribbon {
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 6px 16px;
  border-radius: 0 24px 0 24px;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-small);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-body-small);
  letter-spacing: 0.048px;
  white-space: nowrap;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

.challenge-unit-card--completed .challenge-unit-card__ribbon {
  background: #03925e;
  color: var(--white);
}

.challenge-unit-card--in-progress .challenge-unit-card__ribbon {
  background: var(--blue-light-1);
  color: var(--white);
}

.challenge-unit-card--not-started .challenge-unit-card__ribbon {
  background: var(--neutral-300);
  color: var(--black-body-text);
}

.challenge-unit-card__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
}

.challenge-unit-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--badge-blue-light-1);
  color: var(--blue-light-1);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-headline-small);
}

.challenge-unit-card__heading {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
}

.challenge-unit-card__title {
  flex: 1;
  min-width: 0;
  margin: 0;
  min-height: 88px;
  color: var(--pink-dark-1);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
}

.challenge-unit-card__category {
  flex-shrink: 0;
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-title-large);
}

.challenge-unit-card__category--simulation {
  color: var(--primary-pink);
}

.challenge-unit-card__category--device {
  color: var(--primary-purple);
}

.challenge-unit-card__category--ai {
  color: var(--primary-yellow);
}

.challenge-unit-card__description {
  margin: 0;
  color: var(--black-body-text);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-small);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-headline-small);
}

.challenge-unit-card :deep(.challenge-unit-card__button) {
  margin-top: auto;
  font-size: var(--font-size-title-big);
}

.challenge-unit-card
  :deep(.challenge-unit-card__button .action-button__content) {
  justify-content: space-between;
}

.challenge-unit-card__chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.challenge-unit-card__chevron :deep(svg) {
  width: 9px !important;
  height: 16px !important;
}

@media (max-width: 768px) {
  .challenge-unit-card {
    min-height: 0;
  }

  .challenge-unit-card__title {
    min-height: 0;
    font-size: var(--font-size-headline-large);
    line-height: var(--line-height-headline-large);
  }

  .challenge-unit-card__description {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
  }
}
</style>
